import { ILigaEvent } from '@/model/liga-event.model';
import { ILigaEventRegistration } from '@/model/liga-event-registration.model';
import { phoneForApi } from '@/utils/phone.util';

export interface IAdminPaymentKindSummary {
  paymentKind: 'event' | 'release' | 'subscription';
  count: number;
  totalAmountKopecks: number;
}

export interface IAdminPaymentItem {
  id: number;
  /** event — регистрация; release — покупка релиза; subscription — покупка подписки */
  paymentKind?: 'event' | 'release' | 'subscription';
  ligaEventId?: number | null;
  releaseId?: number | null;
  subscriptionId?: number | null;
  ligaEventTitle: string;
  userId: number;
  userLogin?: string;
  userFirstName?: string;
  userLastName?: string;
  userEmail?: string;
  amountPaidKopecks: number;
  paidAt: string;
  refundedAt?: string | null;
  yooMoneyPaymentId?: string | null;
}

export interface IAdminYooKassaLookup {
  foundInYooKassa: boolean;
  yooStatus?: string | null;
  amountKopecks?: number | null;
  paymentId?: string | null;
  paymentKind?: 'event' | 'release' | 'subscription' | null;
  linkedId?: number | null;
  linkedTitle?: string | null;
  payerName?: string | null;
  payerEmail?: string | null;
  paidAt?: string | null;
  inAdminList: boolean;
  synced: boolean;
  message?: string | null;
}

export interface IAdminReturnItem {
  returnKind: 'event' | 'subscription';
  id: number;
  ligaEventId?: number | null;
  subscriptionId?: number | null;
  title: string;
  userId?: number;
  userLogin?: string;
  userFirstName?: string;
  userLastName?: string;
  userEmail?: string;
  amountPaidKopecks?: number | null;
  paidAt?: string | null;
  cancelledAt?: string | null;
  yooMoneyPaymentId?: string | null;
}

export interface IAdminReturnsSummary {
  eventPendingCount: number;
  subscriptionPendingCount: number;
  totalPendingCount: number;
  eventPendingAmountKopecks?: number;
  subscriptionPendingAmountKopecks?: number;
  totalPendingAmountKopecks?: number;
}
import { Capacitor, CapacitorHttp } from '@capacitor/core';
import { FileTransfer } from '@capacitor/file-transfer';
import { Directory, Filesystem } from '@capacitor/filesystem';
import { Preferences } from '@capacitor/preferences';
import { isPlatform } from '@ionic/vue';
import axios from 'axios';

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const dataUrl = reader.result as string;
      resolve(dataUrl.split(',')[1] || '');
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

const getToken = async (): Promise<string> => {
  const fromStorage = localStorage.getItem('jhi-authenticationToken') || sessionStorage.getItem('jhi-authenticationToken');
  if (fromStorage?.trim()) return fromStorage;
  if (Capacitor.isNativePlatform()) {
    const { value } = await Preferences.get({ key: 'jwt' });
    if (value?.trim()) return value;
  }
  return '';
};

function asArray<T>(data: unknown): T[] {
  if (Array.isArray(data)) return data as T[];
  if (typeof data === 'string') {
    try {
      const parsed = JSON.parse(data);
      return Array.isArray(parsed) ? (parsed as T[]) : [];
    } catch {
      return [];
    }
  }
  return [];
}

function normalizePaymentSummary(data: unknown): IAdminPaymentKindSummary[] {
  if (Array.isArray(data)) return data as IAdminPaymentKindSummary[];
  if (typeof data === 'string') {
    try {
      return normalizePaymentSummary(JSON.parse(data));
    } catch {
      return [];
    }
  }
  if (!data || typeof data !== 'object') return [];

  const obj = data as Record<string, unknown>;
  if (typeof obj.paymentKind === 'string' && obj.count != null) {
    return [obj as unknown as IAdminPaymentKindSummary];
  }

  const kinds = ['event', 'release', 'subscription'] as const;
  const fromKeys: IAdminPaymentKindSummary[] = [];
  for (const kind of kinds) {
    const entry = obj[kind];
    if (entry && typeof entry === 'object') {
      const e = entry as Record<string, unknown>;
      fromKeys.push({
        paymentKind: kind,
        count: Number(e.count ?? 0),
        totalAmountKopecks: Number(e.totalAmountKopecks ?? 0)
      });
    }
  }
  if (fromKeys.length > 0) return fromKeys;

  const arrayLike = Object.keys(obj)
    .filter((k) => /^\d+$/.test(k))
    .sort((a, b) => Number(a) - Number(b))
    .map((k) => obj[k])
    .filter((v) => v && typeof v === 'object' && typeof (v as IAdminPaymentKindSummary).paymentKind === 'string');
  if (arrayLike.length > 0) return arrayLike as IAdminPaymentKindSummary[];

  return [];
}

export function buildPaymentSummaryFromItems(items: IAdminPaymentItem[]): IAdminPaymentKindSummary[] {
  const kinds: Array<'event' | 'release' | 'subscription'> = ['event', 'release', 'subscription'];
  return kinds.map((kind) => {
    const filtered = items.filter((p) => (p.paymentKind ?? inferPaymentKind(p)) === kind);
    return {
      paymentKind: kind,
      count: filtered.length,
      totalAmountKopecks: filtered.reduce((sum, p) => sum + Number(p.amountPaidKopecks ?? 0), 0)
    };
  });
}

function inferPaymentKind(item: IAdminPaymentItem): 'event' | 'release' | 'subscription' {
  if (item.paymentKind) return item.paymentKind;
  if (item.subscriptionId != null) return 'subscription';
  if (item.releaseId != null) return 'release';
  return 'event';
}

const baseUrl = () => process.env.VUE_APP_API_URL || '';

const api = async <T>(method: string, path: string, body?: unknown, params?: Record<string, string>): Promise<T> => {
  if (isPlatform('ios') || isPlatform('android')) {
    const token = await getToken();
    const url = `${baseUrl()}api${path}`;
    const headers: Record<string, string> = { Authorization: `Bearer ${token}` };
    const opts: any = { url, method, headers, readTimeout: 60000 };
    if (body != null) {
      headers['Content-Type'] = 'application/json';
      opts.data = JSON.stringify(body);
    }
    if (params) opts.params = params;
    const res = await CapacitorHttp.request(opts);
    if (res.status >= 400) throw new Error((res.data as any)?.message || `HTTP ${res.status}`);
    return res.data as T;
  }
  const config: any = { url: `api${path}`, method };
  if (body) config.data = body;
  if (params) config.params = params;
  const { data } = await axios.request<T>(config);
  return data;
};

export default {
  /** Список событий: предстоящие; past — прошедшие; all — все (админ/менеджер). */
  async getEvents(options?: boolean | { all?: boolean; past?: boolean }): Promise<ILigaEvent[]> {
    let params: Record<string, string> | undefined;
    if (typeof options === 'boolean') {
      params = options ? { all: 'true' } : undefined;
    } else if (options) {
      params = {};
      if (options.all) params.all = 'true';
      if (options.past) params.past = 'true';
      if (Object.keys(params).length === 0) params = undefined;
    }
    return api<ILigaEvent[]>('GET', '/liga-events', undefined, params);
  },

  async getEvent(id: number): Promise<ILigaEvent | null> {
    try {
      return await api<ILigaEvent>('GET', `/liga-events/${id}`);
    } catch {
      return null;
    }
  },

  /** Регистрации по событию (админ). */
  async getRegistrations(eventId: number): Promise<ILigaEventRegistration[]> {
    return api<ILigaEventRegistration[]>('GET', `/liga-events/${eventId}/registrations`);
  },

  /** Мои регистрации. */
  async getMyRegistrations(): Promise<ILigaEventRegistration[]> {
    return api<ILigaEventRegistration[]>('GET', '/liga-events/my-registrations');
  },

  /** Синхронизировать статус оплаты с ЮKassa (вызов после возврата со страницы оплаты). */
  async syncMyPayment(eventId: number): Promise<boolean> {
    try {
      const result = await api<{ paid?: boolean }>('GET', '/liga-events/sync-my-payment', undefined, { eventId: String(eventId) });
      return result?.paid === true;
    } catch {
      return false;
    }
  },

  async getMyEventPhone(): Promise<string> {
    const response = await api<{ phone?: string }>('GET', '/liga-events/my-phone');
    return response?.phone ?? '';
  },

  /** Регистрация на событие. */
  async register(eventId: number, priceTierId?: number, phone?: string): Promise<ILigaEventRegistration> {
    const body: { priceTierId?: number; phone?: string } = {};
    if (priceTierId != null) body.priceTierId = priceTierId;
    const normalizedPhone = phoneForApi(phone);
    if (normalizedPhone) body.phone = normalizedPhone;
    return api<ILigaEventRegistration>('POST', `/liga-events/${eventId}/register`, body);
  },

  /** Отменить участие (если событие ещё не началось). */
  async cancelRegistration(registrationId: number): Promise<void> {
    await api('DELETE', `/liga-events/registrations/${registrationId}`);
  },

  /** Создать платёж (возвращает redirectUrl). При нескольких тарифах — priceTierId; для чека — customerEmail. В нативном приложении передаёт returnToApp: true для возврата в приложение после оплаты. */
  async createPayment(
    registrationId: number,
    priceTierId?: number,
    customerEmail?: string,
    phone?: string,
    promoCode?: string
  ): Promise<{ paymentId: string | null; redirectUrl: string | null; amountKopecks: number }> {
    const body: {
      priceTierId?: number;
      customerEmail?: string;
      phone?: string;
      returnToApp?: boolean;
      promoCode?: string;
    } = {};
    if (priceTierId != null) body.priceTierId = priceTierId;
    if (customerEmail != null && customerEmail.trim()) body.customerEmail = customerEmail.trim();
    const normalizedPhone = phoneForApi(phone);
    if (normalizedPhone) body.phone = normalizedPhone;
    if (Capacitor.isNativePlatform()) body.returnToApp = true;
    if (promoCode?.trim()) body.promoCode = promoCode.trim();
    return api('POST', `/liga-events/registrations/${registrationId}/create-payment`, Object.keys(body).length ? body : undefined);
  },

  async previewPromo(
    eventId: number,
    promoCode: string,
    priceTierId?: number
  ): Promise<{
    code: string;
    percentOff: number;
    listAmountKopecks: number;
    discountAmountKopecks: number;
    amountPaidKopecks: number;
  }> {
    const body: { promoCode: string; priceTierId?: number } = { promoCode };
    if (priceTierId != null) body.priceTierId = priceTierId;
    return api('POST', `/liga-events/${eventId}/preview-promo`, body);
  },

  /** Список всех оплат для админки (как раньше: все типы в одном ответе, без query-параметров). */
  async getAdminPayments(): Promise<IAdminPaymentItem[]> {
    const data = await api<unknown>('GET', '/admin/payments');
    return asArray<IAdminPaymentItem>(data);
  },

  /** Сводка по типам (только число записей и сумма в копейках). */
  async getAdminPaymentsSummary(): Promise<IAdminPaymentKindSummary[]> {
    try {
      const items = await this.getAdminPayments();
      const fromItems = buildPaymentSummaryFromItems(items);
      if (fromItems.some((s) => s.count > 0)) return fromItems;
    } catch {
      // try summary endpoint below
    }
    try {
      const data = await api<unknown>('GET', '/admin/payments/summary');
      const normalized = normalizePaymentSummary(data);
      if (normalized.length > 0) return normalized;
    } catch {
      // ignore
    }
    return buildPaymentSummaryFromItems([]);
  },

  /** Список оплат одного типа: {@code event}, {@code release} или {@code subscription}. */
  async getAdminPaymentsByKind(kind: 'event' | 'release' | 'subscription'): Promise<IAdminPaymentItem[]> {
    const data = await api<unknown>('GET', '/admin/payments', undefined, { paymentKind: kind });
    return asArray<IAdminPaymentItem>(data);
  },

  /** Массовая синхронизация статусов: все регистрации с yoo_money_payment_id и без paid_at проверяются в ЮKassa. */
  async syncPendingPayments(): Promise<{ checkedCount: number; syncedCount: number }> {
    return api<{ checkedCount: number; syncedCount: number }>('POST', '/admin/payments/sync-pending');
  },

  /** Проверка платежа ЮKassa по id (без изменения БД). */
  async lookupYooKassaPayment(paymentId: string): Promise<IAdminYooKassaLookup> {
    const id = encodeURIComponent(paymentId.trim());
    return api<IAdminYooKassaLookup>('GET', `/admin/payments/yookassa/${id}`);
  },

  /** Синхронизация оплаты по id платежа ЮKassa (при succeeded — обновление БД). */
  async syncYooKassaPayment(paymentId: string): Promise<IAdminYooKassaLookup> {
    const id = encodeURIComponent(paymentId.trim());
    return api<IAdminYooKassaLookup>('POST', `/admin/payments/yookassa/${id}/sync`);
  },

  /** Возврат оплаты (админ). Полный возврат — body пустой; частичный — { amountKopecks: number }. */
  async refundRegistration(registrationId: number, amountKopecks?: number): Promise<void> {
    const body = amountKopecks != null ? { amountKopecks } : undefined;
    await api('POST', `/admin/payments/registrations/${registrationId}/refund`, body);
  },

  /** Отметить оплату как возвращённую вручную (если возврат уже сделан в ЮKassa, а статус не обновился). */
  async markRegistrationRefunded(registrationId: number): Promise<void> {
    await api('POST', `/admin/payments/registrations/${registrationId}/mark-refunded`);
  },

  async refundReleasePurchase(purchaseId: number, amountKopecks?: number): Promise<void> {
    const body = amountKopecks != null ? { amountKopecks } : undefined;
    await api('POST', `/admin/payments/release-purchases/${purchaseId}/refund`, body);
  },

  async markReleasePurchaseRefunded(purchaseId: number): Promise<void> {
    await api('POST', `/admin/payments/release-purchases/${purchaseId}/mark-refunded`);
  },

  async refundSubscriptionPurchase(purchaseId: number, amountKopecks?: number): Promise<void> {
    const body = amountKopecks != null ? { amountKopecks } : undefined;
    await api('POST', `/admin/payments/subscription-purchases/${purchaseId}/refund`, body);
  },

  async markSubscriptionPurchaseRefunded(purchaseId: number): Promise<void> {
    await api('POST', `/admin/payments/subscription-purchases/${purchaseId}/mark-refunded`);
  },

  /** Сводка ожидающих возвратов (для бейджа в меню). */
  async getAdminReturnsSummary(): Promise<IAdminReturnsSummary> {
    return api<IAdminReturnsSummary>('GET', '/admin/returns/summary');
  },

  /** Количество ожидающих возвратов (удобная обёртка). */
  async returnsPendingCount(): Promise<number> {
    try {
      const summary = await this.getAdminReturnsSummary();
      return summary.totalPendingCount ?? 0;
    } catch {
      return 0;
    }
  },

  async getAdminReturns(returnKind?: 'event' | 'subscription'): Promise<IAdminReturnItem[]> {
    const params = returnKind ? { returnKind } : undefined;
    return api<IAdminReturnItem[]>('GET', '/admin/returns', undefined, params);
  },

  async refundReturnEvent(registrationId: number, amountKopecks?: number): Promise<void> {
    const body = amountKopecks != null ? { amountKopecks } : undefined;
    await api('POST', `/admin/returns/events/${registrationId}/refund`, body);
  },

  async markReturnEventRefunded(registrationId: number): Promise<void> {
    await api('POST', `/admin/returns/events/${registrationId}/mark-refunded`);
  },

  async refundReturnSubscription(purchaseId: number, amountKopecks?: number): Promise<void> {
    const body = amountKopecks != null ? { amountKopecks } : undefined;
    await api('POST', `/admin/returns/subscriptions/${purchaseId}/refund`, body);
  },

  async markReturnSubscriptionRefunded(purchaseId: number): Promise<void> {
    await api('POST', `/admin/returns/subscriptions/${purchaseId}/mark-refunded`);
  },

  /** Ответ проверки QR: регистрация и флаг «проход уже был выполнен». */
  async verifyQr(
    eventId: number,
    registrationId: number,
    checkIn?: boolean
  ): Promise<{ registration: ILigaEventRegistration; alreadyCheckedIn: boolean } | null> {
    const params: Record<string, string> = { eventId: String(eventId), registrationId: String(registrationId) };
    if (checkIn) params.checkIn = 'true';
    try {
      const res = await api<{ registration: ILigaEventRegistration; alreadyCheckedIn: boolean }>(
        'GET',
        '/liga-events/verify-qr',
        undefined,
        params
      );
      return res;
    } catch {
      return null;
    }
  },

  /** URL логотипа события. width — опционально, для thumbnail (ускоряет загрузку). */
  logoUrl(logoPath: string | undefined, width?: number): string {
    if (!logoPath) return '';
    const base = baseUrl().replace(/\/$/, '');
    let url = `${base}/api/liga-events/logo?path=${encodeURIComponent(logoPath)}`;
    if (width != null && width > 0) {
      url += `&width=${width}`;
    }
    return url;
  },

  // ——— Админ ———
  async createEvent(event: Partial<ILigaEvent>): Promise<ILigaEvent> {
    return api<ILigaEvent>('POST', '/liga-events', event);
  },

  async updateEvent(id: number, event: Partial<ILigaEvent>): Promise<ILigaEvent> {
    return api<ILigaEvent>('PUT', `/liga-events/${id}`, { ...event, id });
  },

  async deleteEvent(id: number): Promise<void> {
    await api('DELETE', `/liga-events/${id}`);
  },

  async setPriceTiers(eventId: number, tiers: { id?: number; name: string; amountKopecks: number; requiredAuthorityName?: string | null }[]): Promise<ILigaEvent['priceTiers']> {
    return api('PUT', `/liga-events/${eventId}/price-tiers`, tiers);
  },

  async setGiftAuthorities(eventId: number, names: string[]): Promise<string[]> {
    return api('PUT', `/liga-events/${eventId}/gift-authorities`, names);
  },

  async uploadLogo(eventId: number, file: File): Promise<void> {
    const token = await getToken();
    const base = baseUrl().replace(/\/$/, '');
    const url = `${base}/api/liga-events/${eventId}/logo`;

    if (isPlatform('ios') || isPlatform('android')) {
      // На iOS/Android используем @capacitor/file-transfer (multipart по пути к файлу)
      const ext = file.name.split('.').pop() || 'png';
      const tempPath = `logo_upload_${Date.now()}.${ext}`;
      try {
        const base64 = await fileToBase64(file);
        await Filesystem.writeFile({
          path: tempPath,
          data: base64,
          directory: Directory.Cache,
        });
        const { uri } = await Filesystem.getUri({
          path: tempPath,
          directory: Directory.Cache,
        });
        const result = await FileTransfer.uploadFile({
          url,
          path: uri,
          fileKey: 'file',
          headers: { Authorization: `Bearer ${token}` },
          readTimeout: 60000,
          connectTimeout: 60000,
        });
        const code = Number(result.responseCode);
        if (code >= 400) {
          let msg = `HTTP ${code}`;
          if (result.response) {
            try {
              const body = JSON.parse(result.response);
              if (body?.message) msg = body.message;
            } catch {
              // ответ не JSON
            }
          }
          throw new Error(msg);
        }
      } finally {
        try {
          await Filesystem.deleteFile({
            path: tempPath,
            directory: Directory.Cache,
          });
        } catch {
          // игнорируем ошибку удаления временного файла
        }
      }
      return;
    }

    const formData = new FormData();
    formData.append('file', file);
    await axios.post(`api/liga-events/${eventId}/logo`, formData, {
      headers: { Authorization: `Bearer ${token}` },
      timeout: 60000,
    });
  }
};
